/**
 * KeyboardShortcutsDialog — in-UI shortcut reference for the review workspace (UX4).
 * Opened with `?` (outside a text input) or the keyboard icon in the Validate toolbar.
 * MUI Dialog provides focus trap + Escape-to-close; title is wired via aria-labelledby.
 */

import React from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import KeyboardIcon from '@mui/icons-material/Keyboard';

interface ShortcutRow {
  keys: string[];
  description: string;
}

const SHORTCUTS: { group: string; rows: ShortcutRow[] }[] = [
  {
    group: 'Reviewing fields',
    rows: [
      { keys: ['Tab', 'Shift+Tab'], description: 'Next / previous field, lowest confidence first' },
      { keys: ['Enter'], description: 'Confirm field and jump to the next unreviewed field' },
      { keys: ['Ctrl+↓', 'Ctrl+↑'], description: 'Next / previous field in document order' },
      { keys: ['Esc'], description: 'Clear document highlights' },
    ],
  },
  {
    group: 'Completing a document',
    rows: [
      { keys: ['Ctrl+Enter'], description: 'Submit the validated document' },
      { keys: ['Ctrl+.'], description: 'Open the next document (on the Complete screen)' },
    ],
  },
  {
    group: 'Help',
    rows: [{ keys: ['?'], description: 'Show this shortcut reference' }],
  },
];

/** Renders a key combination as keyboard-styled chips. */
const KeyCombo: React.FC<{ combo: string }> = ({ combo }) => (
  <Box
    component="kbd"
    sx={{
      display: 'inline-block',
      px: 1,
      py: 0.25,
      mr: 0.5,
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      backgroundColor: 'action.hover',
      fontFamily: 'inherit',
      fontSize: '0.8125rem',
      fontWeight: 600,
      whiteSpace: 'nowrap',
    }}
  >
    {combo}
  </Box>
);

export interface KeyboardShortcutsDialogProps {
  open: boolean;
  onClose: () => void;
}

const KeyboardShortcutsDialog: React.FC<KeyboardShortcutsDialogProps> = ({ open, onClose }) => (
  <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth aria-labelledby="keyboard-shortcuts-title">
    <DialogTitle id="keyboard-shortcuts-title">
      <Box display="flex" alignItems="center" gap={1}>
        <KeyboardIcon color="primary" aria-hidden="true" />
        Keyboard shortcuts
      </Box>
    </DialogTitle>

    <DialogContent dividers>
      {SHORTCUTS.map((section) => (
        <Box key={section.group} sx={{ mb: 2, '&:last-of-type': { mb: 0 } }}>
          <Typography variant="subtitle2" gutterBottom>
            {section.group}
          </Typography>
          {section.rows.map((row) => (
            <Box
              key={row.description}
              sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2, py: 0.75 }}
            >
              <Typography variant="body2" color="text.secondary">
                {row.description}
              </Typography>
              <Box sx={{ flexShrink: 0 }}>
                {row.keys.map((combo) => (
                  <KeyCombo key={combo} combo={combo} />
                ))}
              </Box>
            </Box>
          ))}
        </Box>
      ))}
    </DialogContent>

    <DialogActions>
      <Button onClick={onClose} variant="contained">
        Close
      </Button>
    </DialogActions>
  </Dialog>
);

export default KeyboardShortcutsDialog;
